import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { Caution, MainHeadline, SubCaution, SubDescription } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { DatetimePicker, RadioButton, SelectBox, TextInput, WysiwygEditor } from 'components/Form'
import { useString, useCsvReader, useWysiwygEditor } from 'hooks/form'
import { FormLabel } from 'components/Label'
import { BlueButton, FileUploadButton, NavButton } from 'components/Button'
import { push } from 'connected-react-router'
import { useDispatch } from 'react-redux'
import { saveMessage } from 'reducks/messages/operations'
import { FirebaseTimestamp } from 'firebase/index'
import { isValidUrlString } from 'lib/validator'
import { addHours, format, isAfter, parse } from 'date-fns'
import { openCautionModal } from 'reducks/modal/operations'
import { fetchCustomerMessage } from 'repos/message'
import { fetchStores } from 'repos/store'
import { parseTimestampToDatetime } from 'lib/helper'
import { StoreType } from 'types/store'

type Props = {
  id?: string
}

const PushCreateTemplate: FC<Props> = (props) => {
  const { id } = props
  const dispatch = useDispatch()

  const { state: title, handleState: handleTitle, setState: setTitle } = useString<string>('')
  const { currentLength, editorState, initEditorState, onEditorStateChange, parseEditorStateToHtml, replaceImage } =
    useWysiwygEditor()
  const { data: userIds, onSelectFile } = useCsvReader()
  const {
    state: transition,
    handleState: _handleTransition,
    setState: setTransition,
  } = useString<'store' | 'external' | 'none'>('store')
  const {
    state: timing,
    handleState: _handleTiming,
    setState: setTiming,
  } = useString<'immediate' | 'scheduled'>('immediate')
  const { state: externalUrl, handleState: handleExternalUrl, setState: setExternalUrl } = useString<string>('')
  const {
    state: scheduledTiming,
    handleState: handleScheduledTiming,
    setState: setScheduledTiming,
  } = useString<string>('')
  const [publishStatus, setPublishStatus] = useState<string>('')
  const [stores, setStores] = useState<StoreType[]>([])
  const [storeId, setStoreId] = useState<string>('')

  const handleTransition = (e: ChangeEvent<HTMLInputElement>) => {
    _handleTransition(e)
    switch (e.target.value) {
      case 'store':
        if (storeId) {
          setExternalUrl(`sacri://store/${storeId}`)
        }
        break
      default:
        setExternalUrl('')
        break
    }
  }

  const handleTiming = (e: ChangeEvent<HTMLInputElement>) => {
    _handleTiming(e)
    if (e.target.value === 'immediate') {
      setScheduledTiming('')
    }
  }

  const handleSaveMessage = async () => {
    await dispatch(
      saveMessage(
        {
          body: '',
          channel: 'sacri',
          external_link: externalUrl,
          has_sound: true,
          html: parseEditorStateToHtml(),
          priority: 'medium',
          publish_status: '1',
          send_time:
            timing === 'immediate' ? FirebaseTimestamp.now() : FirebaseTimestamp.fromDate(new Date(scheduledTiming)),
          title: title,
          user_ids: userIds ?? null,
        },
        id
      )
    )
    timing === 'immediate'
      ? dispatch(push('/admin/push_notification/complete?publishStatus=2'))
      : dispatch(push('/admin/push_notification/complete?publishStatus=1'))
  }

  const handleSaveAsDraft = async () => {
    await dispatch(
      saveMessage(
        {
          body: '',
          channel: 'sacri',
          external_link: externalUrl,
          has_sound: true,
          html: parseEditorStateToHtml(),
          priority: 'medium',
          publish_status: '0',
          send_time:
            timing === 'immediate' ? FirebaseTimestamp.now() : FirebaseTimestamp.fromDate(new Date(scheduledTiming)),
          title: title,
          user_ids: userIds ?? null,
        },
        id
      )
    )
    dispatch(push('/admin/push_notification/complete?publishStatus=0'))
  }

  const handleOpenConfirmModal = useCallback(() => {
    dispatch(
      openCautionModal(
        '配信する',
        'キャンセル',
        `入力された情報をお客様に配信します。\n<span style="color: red">※取り消すことはできないため、内容を確認の上配信を行ってください。</span>`,
        handleSaveMessage,
        `<p style="text-align: left">即時配信を行います。</p>`,
        'blue'
      )
    )
  }, [handleSaveMessage])

  // 入力された`scheduledTiming`の値が現在時刻の１時間以降ならtrue
  const isValidScheduledTiming = (value: string): boolean => {
    if (value === '') return false
    const inputValue = parse(value, "yyyy-MM-dd'T'HH:mm", new Date())
    const now = new Date()
    return isAfter(inputValue, addHours(now, 1))
  }

  const handleStore = (storeId: string) => {
    setStoreId(storeId)
    setExternalUrl(`sacri://store/${storeId}`)
  }

  const storeOptions = stores.map((store) => ({
    value: store.store_id ?? '',
    label: store.name,
  }))

  const isDisabled =
    !title ||
    !currentLength ||
    !transition ||
    !timing ||
    (transition === 'external' && (!externalUrl || !isValidUrlString(externalUrl))) ||
    (timing === 'scheduled' && (!scheduledTiming || !isValidScheduledTiming(scheduledTiming))) ||
    publishStatus === '2'

  useEffect(() => {
    ;(async () => {
      if (!id) return
      const message = await fetchCustomerMessage(id)
      if (!message) return
      setTitle(message.title)
      initEditorState(message.html)
      setExternalUrl(message.external_link)
      setTransition(
        /^sacri:\/\/store\/.+/.test(message.external_link)
          ? 'store'
          : message.external_link.length > 0
          ? 'external'
          : 'none'
      )
      if (message.send_time) {
        setTiming('scheduled')
        setScheduledTiming(parseTimestampToDatetime(message.send_time))
      }
      if (message.publish_status === '2') {
        setPublishStatus(message.publish_status)
      }
      const storeId = message.external_link.split('sacri://store/')
      if (storeId) {
        setStoreId(storeId[1])
      }
    })()
  }, [id])

  useEffect(() => {
    ;(async () => {
      const _stores = await fetchStores(['public'])
      setStores([..._stores])
    })()
  }, [])

  return (
    <div className={styles.standard}>
      <Spacer size={40} />
      <MainHeadline headline={'メッセージを配信'} />
      <Spacer size={24} />
      <SubCaution caution={'sacriユーザー全体へ送信されるため、実行には十分注意してください。'} />
      <Spacer size={24} />
      <TextInput
        disabled={false}
        label={'タイトル'}
        onChange={handleTitle}
        placeholder={'通知タイトルを明記してください'}
        required={true}
        size={'small'}
        type={'text'}
        value={title}
      />
      <Spacer size={20} />
      <FormLabel label={'通知本文'} required={true} />
      <Spacer size={8} />
      <WysiwygEditor
        currentLength={currentLength}
        editorState={editorState}
        folderPath={`admin/notification`}
        maxLength={1000}
        onEditorStateChange={onEditorStateChange}
        replaceImage={replaceImage}
      />
      <Spacer size={68} />
      <FormLabel label={'遷移先'} required={true} />
      <Spacer size={12} />
      <SubDescription description={'メッセージからアプリ内外に遷移させる場合は、下記から選択してください。'} />
      <Spacer size={12} />
      <RadioButton
        disabled={publishStatus === '2'}
        isChecked={transition === 'store'}
        label={'店舗ページへ遷移'}
        onChange={handleTransition}
        value={'store'}
      />
      {transition === 'store' && (
        <>
          <Spacer size={12} />
          <SelectBox options={storeOptions} select={handleStore} value={storeId} />
        </>
      )}
      <Spacer size={12} />
      <RadioButton
        disabled={publishStatus === '2'}
        isChecked={transition === 'external'}
        label={'外部URLを設定する'}
        onChange={handleTransition}
        value={'external'}
      />
      {transition === 'external' && (
        <>
          <Spacer size={12} />
          <TextInput
            disabled={publishStatus === '2'}
            onChange={handleExternalUrl}
            placeholder={'https://sacri.co.jp'}
            required={false}
            size={'small'}
            type={'text'}
            value={externalUrl}
          />
        </>
      )}
      <Spacer size={12} />
      <RadioButton
        disabled={publishStatus === '2'}
        isChecked={transition === 'none'}
        label={'遷移なし'}
        onChange={handleTransition}
        value={'none'}
      />
      <Spacer size={24} />
      <FormLabel label={'配信タイミング'} required={true} />
      <Spacer size={12} />
      <SubDescription description={'メッセージを配信する時刻を選択してください'} />
      <Spacer size={12} />
      <RadioButton
        disabled={publishStatus === '2'}
        isChecked={timing === 'immediate'}
        label={'即時配信を行う'}
        onChange={handleTiming}
        value={'immediate'}
      />
      <Spacer size={12} />
      <RadioButton
        disabled={publishStatus === '2'}
        isChecked={timing === 'scheduled'}
        label={'予約配信を行う'}
        onChange={handleTiming}
        value={'scheduled'}
      />
      {timing === 'scheduled' && (
        <>
          <Spacer size={12} />
          <DatetimePicker
            disabled={publishStatus === '2'}
            min={format(new Date(), "yyyy-MM-dd'T'HH:mm")}
            onChange={handleScheduledTiming}
            value={scheduledTiming}
          />
        </>
      )}
      <Spacer size={12} />
      <FormLabel label={'配信対象'} required={false} />
      <Spacer size={8} />
      <SubDescription description={'対象を絞り込む場合、ユーザーIDが含まれるCSVをアップロードして下さい'} />
      <Spacer size={8} />
      <FileUploadButton
        accept={'text/csv'}
        id={'target-csv'}
        label={userIds ? 'CSVを再アップロードする' : 'CSVをアップロードする'}
        onSelectFile={onSelectFile}
        size={'wide'}
        type={'button'}
      />
      <Spacer size={24} />
      <BlueButton
        disabled={isDisabled}
        label={timing === 'immediate' ? '配信する' : '配信を予約する'}
        onClick={timing === 'immediate' ? handleOpenConfirmModal : handleSaveMessage}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={12} />
      <BlueButton
        disabled={publishStatus === '2' || (timing === 'scheduled' && !scheduledTiming)}
        label={'下書き保存'}
        onClick={handleSaveAsDraft}
        size={'medium'}
        type={'main'}
      />
      {timing === 'scheduled' && (!scheduledTiming || !isValidScheduledTiming(scheduledTiming)) && (
        <>
          <Spacer size={12} />
          <Caution caution={'予約配信は、現在時刻から1時間以上先に設定してください。'} size={'small'} />
        </>
      )}
      <Spacer size={24} />
      <NavButton
        label={'トップページに戻る'}
        iconPath={'/icon/grey/home.svg'}
        onClick={() => dispatch(push('/admin/shops'))}
        url={''}
      />
      <Spacer size={40} />
    </div>
  )
}

export default PushCreateTemplate
